import { Component, ElementRef, NgZone, OnDestroy, inject, viewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { AppLoadService } from "app/app-load.service";
import { MutexService } from "common/concurrency/mutex.service";
import { ProcessVersionService } from "common/version/process-version.service";
import { AuthenticationService } from "core/authentication.service";
import { hasProperty } from "core/functions";
import { MainService } from "core/main.service";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LanguageService } from "core/translate/language.service";
import { FormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule } from "@angular/forms";
import { NgClass, AsyncPipe } from "@angular/common";
import { TrimDirective } from "common/trim.directive";
import { AutofocusDirective } from "common/autofocus/autofocus.directive";
import { SpinComponent } from "common/spin/spin.component";
import { SafePipe } from "common/download/safe.pipe";
import BootstrapProperties = pkit.rest.BootstrapProperties;

interface ErrorMessage {
	key: string;
	args: StringToUnknown;
}

@Component({
	selector: "stages-authentication",
	templateUrl: "./authentication.component.html",
	styleUrls: ["authentication.component.scss"],
	imports: [
		TranslateModule,
		ReactiveFormsModule,
		TrimDirective,
		AutofocusDirective,
		NgClass,
		SpinComponent,
		AsyncPipe,
		SafePipe,
	],
})
export class AuthenticationComponent implements OnDestroy {
	private authService = inject(AuthenticationService);
	private mainService = inject(MainService);
	private mutexService = inject(MutexService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private ngZone = inject(NgZone);
	private processVersionService = inject(ProcessVersionService);
	private translateService = inject(TranslateService);
	private languageService = inject(LanguageService);
	private formBuilder = inject(FormBuilder);

	private destroy$ = new Subject<boolean>();
	bootstrapProperties$: Promise<BootstrapProperties>;

	form!: UntypedFormGroup;
	submitted: boolean = false;
	codeSubmitted: boolean = false;
	rememberUser!: boolean;
	errors!: string;
	samlErrors: string | null;
	securityException: string | null;

	helpDeskInformation?: string;
	helpDeskLinkText?: string;
	helpDeskLinkHref?: string;

	captchaImage?: string;
	captchaToken?: string;

	readonly passwordInput = viewChild("passwordInput", { read: ElementRef });

	readonly codeInput = viewChild("codeInput", { read: ElementRef });

	factor2$ = new BehaviorSubject("NONE");

	inProgess = false;
	ssoInProgess = false;

	constructor() {
		const appLoadService = inject(AppLoadService);

		this.form = this.formBuilder.group({
			username: new UntypedFormControl("", Validators.required),
			password: new UntypedFormControl("", Validators.required),
			captchaText: new UntypedFormControl(false),
			rememberUser: new UntypedFormControl(""),
			code: new UntypedFormControl(""),
		});
		this.setUpHelpdeskInformation();
		this.errors = "";
		this.samlErrors = this.route.snapshot.queryParamMap.get("error");
		this.securityException = this.getSecurityException();
		appLoadService.clearBootstrapProperties();
		this.bootstrapProperties$ = appLoadService.resolveBootstrapProperties();
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}

	passwordAuthentication(): void {
		this.submitted = true;
		this.codeSubmitted = this.factor2$.getValue() !== "NONE";

		if (!this.form.valid) {
			return;
		}
		this.inProgess = true;
		this.errors = "";
		this.mutexService.invoke("login", async () => {
			return this.authService
				.passwordAuthentication(
					this.form.get("username")!.value,
					this.form.get("password")!.value,
					this.form.get("rememberUser")!.value,
					this.form.get("captchaText")!.value,
					this.captchaToken,
					this.form.get("code")!.value,
				)
				.then(this.authSuccess, this.authFail);
		});
	}

	samlAuthentication(): void {
		this.ssoInProgess = true;
		this.authService.invalidateAllCaches();
		this.changeStateOnAck();
	}

	getHrefForExternalLink(linkHref: string): string {
		return linkHref.indexOf("://") > 0 ? linkHref : `http://${linkHref}`;
	}

	getErrorMessages(bootstrapProperties: BootstrapProperties): ErrorMessage | null {
		if (this.securityException !== null) {
			return {
				key: "login.error.nopermission.withReason",
				args: { reason: this.securityException },
			};
		}

		if (bootstrapProperties.maintenance.maintenanceMode === "STARTED") {
			return {
				key: "login.maintenance",
				args: { unavailableUntil: this.formatDate(bootstrapProperties.maintenance.unavailableTo) },
			};
		}

		if (bootstrapProperties.licenseTimeoutSoon) {
			return {
				key: bootstrapProperties.systemLicenseTimeout ? "login.systemlicensetimeoutsoon" : "login.licensetimeoutsoon",
				args: {
					licensePoolName: bootstrapProperties.licensePoolName,
					licenseValidityDate: bootstrapProperties.licenseValidityDate,
				},
			};
		}

		return null;
	}

	private authSuccess = (): void => {
		//do not stop spinner, instead just do the redirect
		this.changeStateOnAck();
	};

	private authFail = (response: unknown): void => {
		if (this.codeSubmitted) {
			this.codeInput()?.nativeElement.focus();
		} else {
			this.passwordInput()?.nativeElement.focus();
		}
		this.inProgess = false;
		this.securityException = this.getSecurityException();
		if (response && hasProperty(response, "error")) {
			const error = response.error;
			if (hasProperty(error, "captchaImage") && hasProperty(error, "captchaToken")) {
				this.form.get("captchaText")?.setValue("");
				this.captchaImage = error.captchaImage as string;
				this.captchaToken = error.captchaToken as string;
			}
			if (hasProperty(error, "otpType") && error.otpType !== null) {
				this.factor2$.next(error.otpType as string);
			}
			if (
				hasProperty(error, "messages") &&
				error.messages instanceof Array &&
				error.messages[0] &&
				error.messages[0].keys &&
				error.messages[0].keys[0]
			) {
				this.errors = error.messages[0].keys[0];
			} else {
				// eslint-disable-next-line @typescript-eslint/only-throw-error -- TODO: Remove this comment and fix warnings!
				throw response;
			}
		}
	};

	private changeStateOnAck(): void {
		this.mainService.onLoginCallback();
		if (this.route.snapshot.queryParamMap.has("redirectTo")) {
			// Error 34300, without zone an empty page will be displayed
			this.ngZone.run(async () => this.router.navigateByUrl(this.route.snapshot.queryParamMap.get("redirectTo")!));
		} else {
			this.mainService.getApplication("_vv").then(async (application) => {
				const pv = await this.processVersionService.getInitialProcessVersionIdentifier();
				this.router.navigate(["workspace", application.homeWorkspace.id, pv, "home"]);
			});
		}
	}

	private formatDate(date: string | null | undefined): string {
		if (date === null || date === undefined) {
			return "?";
		}
		try {
			return new Date(Date.parse(date)).toLocaleString(this.languageService.getCurrentLanguage());
		} catch (e: unknown) {
			console.error(e);
			return date.toString();
		}
	}

	private setUpHelpdeskInformation(): void {
		const translatedInformation$: Observable<string> = this.translateService.get("login.help.information");
		const translatedLinkText$: Observable<string> = this.translateService.get("login.help.link.text");
		const translatedLinkHref$: Observable<string> = this.translateService.get("login.help.link.href");

		translatedInformation$.pipe(takeUntil(this.destroy$)).subscribe((information) => {
			this.helpDeskInformation = this.isPropertyDefined(information) ? information : undefined;
		});

		translatedLinkText$.pipe(takeUntil(this.destroy$)).subscribe((linkText) => {
			this.helpDeskLinkText = this.isPropertyDefined(linkText) ? linkText : undefined;
		});

		translatedLinkHref$.pipe(takeUntil(this.destroy$)).subscribe((linkHref) => {
			this.helpDeskLinkHref = this.isPropertyDefined(linkHref) ? linkHref : undefined;
		});
	}

	private isPropertyDefined(value: string): boolean {
		return !value.startsWith("???");
	}

	private getSecurityException(): string | null {
		return this.route.snapshot.queryParamMap.get("securityException");
	}

	isRequestRunning(): boolean {
		return this.inProgess;
	}

	isSSORequestRunning(): boolean {
		return this.ssoInProgess;
	}
}
